import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import {
  createTemplate,
  setShouldCreate,
} from "../../../../redux/Slice/Templates/templateCreationSlice";
import { updateTemplate } from "../../../../redux/Slice/Templates/templateSlice";
import TemplateCreationPreview from "../Preview/TemplateCreationPreview";

const TemplateSubmission = () => {
  const dispatch = useDispatch();

  // Pull relevant data from templateCreation slice
  const {
    selectedCategory,
    templateId,
    language,
    allowAutoCategoryChange,
    header,
    headerData,
    body,
    bodyVariables,
    footer,
    buttons, // { quickReplies, phoneButtons, linkButtons, ... }
    carouselCards, // Only used if marketing + user chose carousel
    shouldCreate,
    status: createStatus,
    error: createError,
    responseData, // Full response from the backend (create only)
    editMode,
    editTemplateId,
  } = useSelector((state) => state.templateCreation);
  // In edit mode the submit result lives in the template slice's updateTemplate
  // thunk instead — createTemplate/updateTemplate are two separate async flows.
  const updateStatus = useSelector((state) => state.template.updateStatus);
  const updateError = useSelector((state) => state.template.updateError);
  const status = editMode ? updateStatus : createStatus;
  const error = editMode ? updateError : createError;

  // True when this submission will use the CAROUSEL component (Marketing
  // category + at least one card). Buttons then live on each card
  // (carouselCards[i].buttons) instead of the top-level `buttons` object,
  // and there's no top-level HEADER — used by both the payload builder and
  // the review summary below so they stay in sync.
  const isCarouselMode =
    selectedCategory?.toUpperCase() === "MARKETING" && carouselCards?.length > 0;

  /**
   * Build final object to send to the server.
   * Adapt this to fit your actual API's structure.
   */
  const buildTemplatePayload = () => {
    // Basic required fields
    const templateData = {
      name: templateId, // e.g. "my_template_1"
      language: language, // e.g. "en"
      category: selectedCategory?.toUpperCase() || "UTILITY",
      allowAutoCategoryChange: !!allowAutoCategoryChange,
    };

    // We'll store "components" in an array, or adapt to your structure
    const components = [];

    // ========== HEADER ==========
    // Carousel templates only support BODY + CAROUSEL at the top level —
    // Meta rejects a top-level HEADER alongside a CAROUSEL component. Each
    // card carries its own media header instead (built further below).
    if (header && header !== "none" && !isCarouselMode) {
      let format;
      switch (header) {
        case "image":
          format = "IMAGE";
          break;
        case "video":
          format = "VIDEO";
          break;
        case "document":
          format = "DOCUMENT";
          break;
        case "text":
          format = "TEXT";
          break;
        default:
          format = "NONE";
      }

      const headerComponent = {
        type: "HEADER",
        format,
      };

      // If there's data for the header (like file URL or text)
      if (headerData) {
        // For images/docs/videos, you might put it in example/header_handle, etc.
        if (format === "IMAGE" || format === "VIDEO" || format === "DOCUMENT") {
          headerComponent.example = { header_handle: [headerData] };
        } else if (format === "TEXT") {
          headerComponent.text = headerData;
        }
      }
      components.push(headerComponent);
    }

    // ========== BODY ==========
    if (body) {
      const bodyComponent = {
        type: "BODY",
        text: body,
      };
      // If there are placeholders with bodyVariables
      const bodyValues = (bodyVariables || []).map((bv) => bv.value);
      if (bodyValues.length > 0) {
        // E.g., if your API expects "example": { "body_text": [ ... ] }
        bodyComponent.example = { body_text: [bodyValues] };
      }
      components.push(bodyComponent);
    }

    // ========== FOOTER ==========
    // Same rule as HEADER above — carousel templates only support BODY +
    // CAROUSEL at the top level, no FOOTER on the main message bubble.
    if (footer && !isCarouselMode) {
      components.push({
        type: "FOOTER",
        text: footer,
      });
    }

    // ========== MARKETING: CAROUSEL ==========
    if (isCarouselMode) {
      const carouselComponent = {
        type: "carousel",
        cards: carouselCards.map((card) => {
          const cardComponents = [];

          // Add header if present
          if (card.header && card.headerData) {
            cardComponents.push({
              type: "header",
              format: card.header,
              example: {
                header_handle: [card.headerData],
              },
            });
          }

          // Add body
          if (card.body) {
            const bodyComponent = {
              type: "body",
              text: card.body,
            };
            if (card.bodyVariables?.length > 0) {
              bodyComponent.example = {
                body_text: [card.bodyVariables.map((bv) => bv.value)],
              };
            }
            cardComponents.push(bodyComponent);
          }

          // Add buttons
          if (card.buttons?.length > 0) {
            const buttonsComponent = {
              type: "buttons",
              buttons: card.buttons.map((btn) => {
                if (btn.type === "quickReply") {
                  return { type: "quick_reply", text: btn.label };
                }

                if (btn.type === "phone") {
                  return {
                    type: "phone_number",
                    text: btn.label,
                    phone_number:
                      (btn.countryCode || "") + (btn.phoneNumber || ""),
                  };
                }

                // "link"
                const button = { type: "url", text: btn.label, url: btn.link };
                if (btn.isLinkDynamic && btn.dynamicVariableLabel) {
                  button.example = [btn.dynamicVariableLabel];
                }
                return button;
              }),
            };
            cardComponents.push(buttonsComponent);
          }

          return {
            components: cardComponents,
          };
        }),
      };

      components.push(carouselComponent);
    } else {
      // ========== BUTTONS (Normal) ==========
      const allButtons = [];
      buttons.quickReplies?.forEach((qr) => {
        allButtons.push({
          type: "QUICK_REPLY",
          text: qr.label,
        });
      });
      buttons.phoneButtons?.forEach((phoneBtn) => {
        allButtons.push({
          type: "PHONE_NUMBER",
          text: phoneBtn.label,
          phone_number:
            (phoneBtn.countryCode || "") + (phoneBtn.phoneNumber || ""),
        });
      });
      buttons.linkButtons?.forEach((linkBtn) => {
        // Check if it's a dynamic link
        const isDynamic = linkBtn.isLinkDynamic;

        // Create the base button object
        const buttonPayload = {
          type: "URL",
          text: linkBtn.label,
          url: linkBtn.link,
        };

        // For dynamic URLs:
        // 1. Don't include the {1} in the URL itself
        // 2. Add example field with the dynamic variable value
        if (isDynamic) {
          // Add example field with the value from dynamicVariableLabel
          if (linkBtn.dynamicVariableLabel) {
            buttonPayload.example = [linkBtn.dynamicVariableLabel];
          }
        }

        allButtons.push(buttonPayload);
      });

      // If we have normal buttons, push them into components
      if (allButtons.length > 0) {
        components.push({ type: "BUTTONS", buttons: allButtons });
      }
    }

    // Attach components if we have any
    if (components.length > 0) {
      templateData.components = components;
    }

    return templateData;
  };

  /**
   * Handle the actual dispatch to create the template.
   */
  const createUnifiedTemplate = () => {
    const finalPayload = buildTemplatePayload();

    const submission = editMode
      ? dispatch(
          updateTemplate({
            id: editTemplateId,
            category: finalPayload.category,
            components: finalPayload.components,
          })
        )
      : dispatch(createTemplate(finalPayload));

    submission
      .unwrap()
      .then(() => {
        // Prevent repeated submission if the user remains on the page
        dispatch(setShouldCreate(false));
      })
      .catch((err) => {
        // Prevent repeated submission if the user remains on the page
        dispatch(setShouldCreate(false));
      });
  };

  /**
   * Watch for `shouldCreate` becoming `true`.
   * Then run the create, and set it back to false.
   */
  useEffect(() => {
    if (shouldCreate) {
      // Immediately set false so it won't re-trigger
      dispatch(setShouldCreate(false));
      // Defense-in-depth: the Finish button already disables itself while
      // a submission is in flight, but skip firing a second non-idempotent
      // create/update request here too in case shouldCreate is ever set
      // while one is still pending.
      if (status !== "loading") {
        createUnifiedTemplate();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCreate]);

  return (
    <Box sx={{ padding: 2 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ color: (theme) => theme.palette.text.primary }}
      >
        {editMode ? "Template Update" : "Template Submission"} for {selectedCategory?.toUpperCase()}
      </Typography>

      {/* Loading Spinner */}
      {status === "loading" && (
        <Box sx={{ my: 2 }}>
          <CircularProgress
            sx={{ color: (theme) => theme.palette.primary.main }}
          />
        </Box>
      )}

      {/* Error Alert */}
      {status === "failed" && error && (
        <Alert severity="error" sx={{ my: 2 }}>
          <strong>Error:</strong>{" "}
          {typeof error === "string"
            ? error
            : error?.userFriendlyMessage || 
              error?.message ||
              error?.details?.error?.error_user_msg ||
              "Failed to create template. Please try again."}
          
          {/* Show solution if available */}
          {error?.solution && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Solution:
              </Typography>
              <Typography variant="body2">
                {error.solution}
              </Typography>
            </Box>
          )}
          
          {/* Show error code for debugging */}
          {error?.errorCode && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Error Code: {error.errorCode}
              </Typography>
            </Box>
          )}
        </Alert>
      )}

      {/* Success Alert */}
      {status === "succeeded" && editMode && (
        <Alert severity="success" sx={{ my: 2 }}>
          Template updated successfully!
        </Alert>
      )}
      {status === "succeeded" && !editMode && responseData && (
        <Alert severity="success" sx={{ my: 2 }}>
          {responseData.categoryShifted ? (
            <>
              <strong>Template created successfully as {responseData.actualCategory} type!</strong>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Note: Template was shifted from {responseData.requestedCategory} to MARKETING category due to WhatsApp policy rules.
                </Typography>
              </Box>
            </>
          ) : (
            "Template created successfully!"
          )}
        </Alert>
      )}

      {/* Name & Language */}
      <Typography
        variant="body1"
        sx={{ color: (theme) => theme.palette.text.primary }}
      >
        <strong>Name (templateId):</strong> {templateId || "N/A"}
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: (theme) => theme.palette.text.primary }}
      >
        <strong>Language:</strong> {language || "N/A"}
      </Typography>

      {/* Preview — shown here inline for this last step instead of the
          right-side panel (TemplatePreview.jsx renders nothing on this
          step so it isn't duplicated there). */}
      <Box sx={{ mt: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, color: (theme) => theme.palette.text.primary }}
        >
          Preview
        </Typography>
        <TemplateCreationPreview />
      </Box>
    </Box>
  );
};

export default TemplateSubmission;
